import { safeParseDecimal } from '@/lib/api/common';
import type { AggregationFn } from '@tanstack/react-table';

export const sum: AggregationFn<any> = (columnId, leafRows, childRows) => {
  return leafRows.reduce((sum, row) => {
    const value = row.getValue(columnId);
    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
};

export const sumDecimal: AggregationFn<any> = (columnId, leafRows, childRows) => {
  return leafRows.reduce((sum, row) => {
    const value = row.getValue(columnId);
    if (!value) return sum;
    return sum.plus(safeParseDecimal({})(value as any));
  }, safeParseDecimal({})(0)).toNumber();
};

export const average: AggregationFn<any> = (columnId, leafRows, childRows) => {
  const total = leafRows.reduce((sum, row) => {
    const value = row.getValue(columnId);
    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
  return leafRows.length ? total / leafRows.length : 0;
};

export const count: AggregationFn<any> = (columnId, leafRows, childRows) => {
  return leafRows.length;
};

export const min: AggregationFn<any> = (columnId, leafRows, childRows) => {
  let min: number | undefined;
  leafRows.forEach(row => {
    const value = row.getValue(columnId);
    if (typeof value === 'number' && (min === undefined || value < min)) {
      min = value;
    }
  });
  return min ?? 0;
};

export const max: AggregationFn<any> = (columnId, leafRows, childRows) => {
  let max: number | undefined;
  leafRows.forEach(row => {
    const value = row.getValue(columnId);
    if (typeof value === 'number' && (max === undefined || value > max)) {
      max = value;
    }
  });
  return max ?? 0;
};

export const uniqueCount: AggregationFn<any> = (columnId, leafRows, childRows) => {
  const unique = new Set();
  leafRows.forEach(row => {
    const value = row.getValue(columnId);
    if (value !== null && value !== undefined) {
      unique.add(value);
    }
  });
  return unique.size;
};

export const percentage: AggregationFn<any> = (columnId, leafRows, childRows) => {
  const trueCount = leafRows.reduce((count, row) => {
    const value = row.getValue(columnId);
    return count + (value === true ? 1 : 0);
  }, 0);
  return leafRows.length ? (trueCount / leafRows.length) * 100 : 0;
};

export const concatenate: AggregationFn<any> = (columnId, leafRows, childRows) => {
  const values = new Set<string>();
  leafRows.forEach(row => {
    const value = row.getValue(columnId);
    if (value !== null && value !== undefined && value !== '') {
      values.add(String(value));
    }
  });
  return Array.from(values).join(', ');
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const formatNumber = (value: number, decimals: number = 0): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};
